import { useEffect, useMemo, useState } from 'react';
import { Card, Field, Modal, Skeleton } from '../../components/UI';
import { fetchReportData } from '../../firebase/repo';
import { displayDate, formatMoney, monthKey, todayISO } from '../../utils/date';
import { buildFinancialHealthModel, type FinancialHealthDomain, type FinancialHealthIssue, type FinancialHealthSeverity } from '../../utils/financialHealth';
import type { PageKey } from '../../app/AppShell';

type SourceData = Awaited<ReturnType<typeof fetchReportData>>;

const emptySource: SourceData = { transactions: [], payments: [], plans: [], crdr: [], budgets: [], accounts: [], accountLedger: [], balanceAudits: [], monthlyPeriods: [] };
const severityOptions: Array<'all' | FinancialHealthSeverity> = ['all', 'critical', 'warning', 'info'];
const domainOptions: Array<'all' | FinancialHealthDomain> = ['all', 'transaction', 'account', 'transfer', 'card_payment', 'plan', 'crdr', 'budget', 'reconciliation', 'configuration', 'period'];

const actionTarget = (issue: FinancialHealthIssue): PageKey => {
  if (issue.domain === 'plan') return 'upcoming';
  if (issue.domain === 'budget' || issue.domain === 'configuration') return 'setup';
  if (issue.domain === 'reconciliation' || issue.domain === 'period') return 'monthlyClose';
  if (issue.domain === 'crdr') return 'records';
  if (issue.domain === 'transaction') return 'records';
  if (issue.domain === 'account' || issue.domain === 'transfer' || issue.domain === 'card_payment') return 'setup';
  return 'home';
};

function HealthMetric({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return <div className={`report-metric ${tone || ''}`}><span>{label}</span><b>{value}</b></div>;
}

function SeverityBadge({ severity }: { severity: FinancialHealthSeverity }) {
  return <span className={`health-severity ${severity}`}>{severity}</span>;
}

export function FinancialHealthPage({ uid, onNavigate }: { uid: string; onNavigate: (page: PageKey) => void }) {
  const [data, setData] = useState<SourceData>(emptySource);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [severity, setSeverity] = useState<'all' | FinancialHealthSeverity>('all');
  const [domain, setDomain] = useState<'all' | FinancialHealthDomain>('all');
  const [month, setMonth] = useState(monthKey(todayISO()));
  const [selectedIssue, setSelectedIssue] = useState<FinancialHealthIssue | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchReportData(uid));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Financial health load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [uid]);

  const model = useMemo(() => buildFinancialHealthModel(data, todayISO()), [data]);
  const visibleIssues = model.issues.filter((issue) => severity === 'all' || issue.severity === severity)
    .filter((issue) => domain === 'all' || issue.domain === domain)
    .filter((issue) => !month || !issue.monthKey || issue.monthKey === month);

  return <div className="page health-page reports-insights-page">
    <Card className="reports-hero health-hero">
      <div className="reports-hero-head">
        <div>
          <p className="eyebrow">Financial control layer</p>
          <h1>Financial Health</h1>
          <p className="muted">Review data quality, reconciliation, commitments, and configuration issues. This page never auto-fixes or mutates finance data.</p>
        </div>
        <button className="btn" disabled={loading} onClick={load}>Refresh</button>
      </div>
      {loading ? <Skeleton /> : <div className="reports-metric-grid">
        <HealthMetric label="Overall status" value={model.status} tone={model.status === 'Critical' ? 'red' : model.status === 'Needs Attention' ? 'orange' : model.status === 'Review Recommended' ? 'blue' : 'green'} />
        <HealthMetric label="Critical" value={model.issueCounts.critical} tone="red" />
        <HealthMetric label="Warnings" value={model.issueCounts.warning} tone="orange" />
        <HealthMetric label="Info" value={model.issueCounts.info} tone="blue" />
        <HealthMetric label="Evaluated" value={displayDate(model.evaluatedAtISO.slice(0, 10))} />
      </div>}
      {error && <p className="warning">{error}</p>}
    </Card>

    <Card>
      <div className="section-title"><div><h3>Analysis scope</h3><p className="muted">Transparent rule scope for this diagnostic pass.</p></div></div>
      <div className="health-scope-grid">
        {Object.entries(model.analysisScope).map(([key, value]) => <div key={key}><b>{key.replace(/([A-Z])/g, ' $1')}</b><span>{value}</span></div>)}
      </div>
    </Card>

    <Card>
      <div className="section-title"><div><h3>Filters</h3><p className="muted">Derived issues disappear when source data is corrected; there is no fake resolved state.</p></div></div>
      <div className="health-filter-row">
        <Field label="Severity"><select value={severity} onChange={(event) => setSeverity(event.target.value as typeof severity)}>{severityOptions.map((item) => <option key={item} value={item}>{item}</option>)}</select></Field>
        <Field label="Domain"><select value={domain} onChange={(event) => setDomain(event.target.value as typeof domain)}>{domainOptions.map((item) => <option key={item} value={item}>{item.replace('_', ' ')}</option>)}</select></Field>
        <Field label="Month"><input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></Field>
        <button className="btn small" onClick={() => { setSeverity('all'); setDomain('all'); setMonth(''); }}>All issues</button>
      </div>
    </Card>

    {loading ? <Card><Skeleton /></Card> : <>
      <Card>
        <div className="section-title"><div><h3>Priority Review</h3><p className="muted">{visibleIssues.length} issue(s) after filters, sorted by severity and impact.</p></div></div>
        <div className="health-issue-list">
          {visibleIssues.map((issue) => <IssueCard key={issue.id} issue={issue} onDetails={() => setSelectedIssue(issue)} onNavigate={() => onNavigate(actionTarget(issue))} />)}
          {!visibleIssues.length && <p className="compact-empty">No financial-health issues match these filters.</p>}
        </div>
      </Card>

      <div className="health-group-grid">
        {model.groups.map((group) => <Card key={group.domain}>
          <div className="section-title"><div><h3>{group.title}</h3><p className="muted">{group.issues.length} issue(s)</p></div></div>
          {group.issues.slice(0, 4).map((issue) => <button className={`health-mini-issue ${issue.severity}`} key={issue.id} onClick={() => setSelectedIssue(issue)}><span><b>{issue.title}</b><small>{issue.description}</small></span><SeverityBadge severity={issue.severity} /></button>)}
        </Card>)}
      </div>

      <Card>
        <div className="section-title"><div><h3>Finance Signals</h3><p className="muted">Diagnostics only; no balances are changed from this view.</p></div></div>
        <div className="metric-strip">
          <div className="metric credit"><span>Receivable</span><b>{formatMoney(model.summaries.receivable)}</b></div>
          <div className="metric debit"><span>Payable</span><b>{formatMoney(model.summaries.payable)}</b></div>
          <div className="metric orange"><span>Overdue plans</span><b>{formatMoney(model.summaries.overduePlanAmount)}</b></div>
          <div className="metric"><span>Budget overspend</span><b>{formatMoney(model.summaries.budgetOverspend)}</b></div>
        </div>
      </Card>
    </>}

    <Modal open={!!selectedIssue} title={selectedIssue?.title || 'Issue details'} onClose={() => setSelectedIssue(null)}>
      {selectedIssue && <div className="health-detail">
        <div className="confirm-details">
          <p><b>Severity</b><span>{selectedIssue.severity}</span></p>
          <p><b>Domain</b><span>{selectedIssue.domain.replace('_', ' ')}</span></p>
          <p><b>Code</b><span>{selectedIssue.code}</span></p>
          {selectedIssue.amount !== undefined && <p><b>Amount</b><span>{formatMoney(selectedIssue.amount)}</span></p>}
          {selectedIssue.count !== undefined && <p><b>Count</b><span>{selectedIssue.count}</span></p>}
        </div>
        <p className="muted">{selectedIssue.description}</p>
        <p><b>Recommended action:</b> {selectedIssue.recommendedAction || 'Review source data'}</p>
        {selectedIssue.evidence?.length ? <div className="table-wrap"><table className="data-table"><thead><tr>{Object.keys(selectedIssue.evidence[0]).map((key) => <th key={key}>{key}</th>)}</tr></thead><tbody>{selectedIssue.evidence.map((row, index) => <tr key={index}>{Object.keys(selectedIssue.evidence?.[0] || {}).map((key) => <td key={key}>{row[key] ?? '-'}</td>)}</tr>)}</tbody></table></div> : <p className="compact-empty">No row-level evidence is required for this issue.</p>}
        <div className="confirm-actions"><button className="btn" onClick={() => setSelectedIssue(null)}>Close</button><button className="btn primary" onClick={() => { onNavigate(actionTarget(selectedIssue)); setSelectedIssue(null); }}>{selectedIssue.recommendedAction || 'Open Related Page'}</button></div>
      </div>}
    </Modal>
  </div>;
}

function IssueCard({ issue, onDetails, onNavigate }: { issue: FinancialHealthIssue; onDetails: () => void; onNavigate: () => void }) {
  return <div className={`health-issue-card ${issue.severity}`}>
    <div>
      <SeverityBadge severity={issue.severity} />
      <h3>{issue.title}</h3>
      <p>{issue.description}</p>
      <small>{issue.domain.replace('_', ' ')} / {issue.code}{issue.monthKey ? ` / ${issue.monthKey}` : ''}</small>
    </div>
    <div className="health-issue-meta">
      {issue.count !== undefined && <b>{issue.count} item(s)</b>}
      {issue.amount !== undefined && <strong>{formatMoney(issue.amount)}</strong>}
      <button className="btn small" onClick={onDetails}>View Details</button>
      <button className="btn small primary" onClick={onNavigate}>{issue.recommendedAction || 'Review'}</button>
    </div>
  </div>;
}
